/**
 * Benchmark Period Analysis Card for Accountant Report View
 * Displays benchmark period details and selection rationale
 */

import React from 'react';

interface BenchmarkPeriod {
  start?: string;
  end?: string;
  type?: string;
  revenue?: number;
  variable_profit?: number;
  why_selected?: string;
}

interface BenchmarkPeriodCardProps {
  benchmarkPeriod: BenchmarkPeriod;
  formatCurrency: (amount?: number) => string;
}

export default function BenchmarkPeriodCard({
  benchmarkPeriod,
  formatCurrency
}: BenchmarkPeriodCardProps) {
  if (!benchmarkPeriod) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">📊</span>
        Benchmark Period Analysis
      </h2>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-1">Period</p>
            <p className="text-lg font-bold text-blue-900">
              {benchmarkPeriod.start} to {benchmarkPeriod.end}
            </p>
            <p className="text-xs text-blue-700 mt-1">{benchmarkPeriod.type}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-green-800 mb-1">Revenue</p>
            <p className="text-lg font-bold text-green-900">
              {formatCurrency(benchmarkPeriod.revenue)}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-800 mb-1">Variable Profit</p>
            <p className="text-lg font-bold text-purple-900">
              {formatCurrency(benchmarkPeriod.variable_profit)}
            </p>
          </div>
        </div>
        {benchmarkPeriod.why_selected && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 font-medium mb-1">Selection Rationale:</p>
            <p className="text-sm text-gray-600">{benchmarkPeriod.why_selected}</p>
          </div>
        )}
      </div>
    </div>
  );
}
